// Per-OT traceability page. Inspired by the legacy
// civ-exporter-portal delivery-detail page but scaled up to cover the
// entire OT shipment (multiple deliveries, producers and parcels
// deduped). Tabs: Résumé / Producteurs / Parcelles / Livraisons.

import { useMemo, useState } from "react"
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  LayoutDashboard,
  Loader2,
  MapPin,
  Receipt,
  Ship,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"

import { BreadcrumbNav } from "@/components/breadcrumb-nav"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { TraceabilityDeliveriesTab } from "@/features/transit/components/traceability/deliveries-tab"
import { TraceabilityParcelsTab } from "@/features/transit/components/traceability/parcels-tab"
import { TraceabilityProducersTab } from "@/features/transit/components/traceability/producers-tab"
import { TraceabilitySummaryTab } from "@/features/transit/components/traceability/summary-tab"
import { TraceabilityTransactionsTab } from "@/features/transit/components/traceability/transactions-tab"
import {
  useTransitOrder,
  useTransitTraceability,
} from "@/features/transit/hooks/use-transit-orders"
import { exportTraceabilityBundle } from "@/features/transit/lib/traceability-bundle"
import type { OTStage } from "@/features/transit/api/types"
import { resolveApiError } from "@/lib/api/error-i18n"
import { paths } from "@/routes/paths"

export function TransitTraceabilityPage() {
  const { otId } = useParams<{ otId: string }>()
  const { t } = useTranslation("transit")
  const navigate = useNavigate()

  const ot = useTransitOrder(otId)
  const trace = useTransitTraceability(otId)
  const [activeTab, setActiveTab] = useState("summary")
  const [exporting, setExporting] = useState(false)

  const eudrTone = useMemo(() => {
    const status = trace.data?.summary.deliveries.status
    if (status === "PASS") {
      return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
    }
    if (status === "FAIL") {
      return "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
    }
    return "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
  }, [trace.data])

  if (ot.isLoading || (!ot.data && !ot.error)) {
    return <PageSkeleton />
  }
  if (ot.error || !ot.data) {
    return (
      <div className="flex flex-col gap-3 p-6">
        <p className="text-sm text-muted-foreground">
          {t("detail.notFound", { defaultValue: "Ordre de transit introuvable." })}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(paths.transitOrders)}
          className="self-start"
        >
          <ArrowLeft className="size-4" />
          {t("detail.back")}
        </Button>
      </div>
    )
  }

  const otRow = ot.data
  const summary = trace.data?.summary
  const counts = {
    producers: summary?.producers.total ?? 0,
    parcels: summary?.parcels.total ?? 0,
    deliveries: summary?.deliveries.total ?? 0,
    transactions: summary?.purchases.total ?? 0,
  }

  const handleExport = async () => {
    if (exporting) return
    setExporting(true)
    try {
      // Pass the cached trace if we have it; the bundle helper will
      // fetch inline when undefined, so a cold export still works.
      const result = await exportTraceabilityBundle(
        otRow,
        trace.data,
        (stage: OTStage) => t(`stages.${stage}`),
      )
      if (result.failedFiles > 0) {
        toast.warning(
          t("terminer.bundlePartial", {
            failed: result.failedFiles,
            total: result.totalFiles,
          }),
        )
      } else {
        toast.success(t("terminer.bundleReady"))
      }
    } catch (error) {
      toast.error(resolveApiError(error, t))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="flex flex-col gap-5 p-6">
      <BreadcrumbNav
        crumbs={[
          { label: t("list.title"), to: paths.transitOrders },
          {
            label: otRow.otNumber,
            to: paths.transitOrderDetail(otRow.id),
          },
          { label: t("trace.pageTitle") },
        ]}
      />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("trace.pageTitle")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {otRow.otNumber} · {otRow.clientName}
            {otRow.contractNumber ? <> · {otRow.contractNumber}</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {summary ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${eudrTone}`}
            >
              {summary.deliveries.status === "PASS" ? (
                <CheckCircle2 className="size-3.5" />
              ) : (
                <AlertTriangle className="size-3.5" />
              )}
              {t(`terminer.eudrStatus.${summary.deliveries.status}`)}
            </span>
          ) : null}
          <Button asChild variant="ghost" size="sm">
            <Link to={paths.transitOrderDetail(otRow.id)}>
              <ArrowLeft className="size-4" />
              {t("detail.back")}
            </Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={exporting || !trace.data}
          >
            {exporting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
            {exporting
              ? t("terminer.bundlePending")
              : t("terminer.downloadDataBtn")}
          </Button>
        </div>
      </header>

      {trace.isError ? (
        <Card className="flex items-center gap-3 border-red-200 bg-red-50/60 p-4 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <span>{resolveApiError(trace.error, t)}</span>
        </Card>
      ) : null}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="summary">
            <LayoutDashboard className="size-4" />
            {t("trace.tabs.summary")}
          </TabsTrigger>
          <TabsTrigger value="producers">
            <Users className="size-4" />
            {t("trace.tabs.producers", { count: counts.producers })}
          </TabsTrigger>
          <TabsTrigger value="parcels">
            <MapPin className="size-4" />
            {t("trace.tabs.parcels", { count: counts.parcels })}
          </TabsTrigger>
          <TabsTrigger value="deliveries">
            <Ship className="size-4" />
            {t("trace.tabs.deliveries", { count: counts.deliveries })}
          </TabsTrigger>
          <TabsTrigger value="transactions">
            <Receipt className="size-4" />
            {t("trace.tabs.transactions", { count: counts.transactions })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="pt-4">
          {trace.isLoading || !trace.data ? (
            <TabSkeleton />
          ) : (
            <TraceabilitySummaryTab ot={otRow} traceability={trace.data} />
          )}
        </TabsContent>
        <TabsContent value="producers" className="pt-4">
          {trace.isLoading || !trace.data ? (
            <TabSkeleton />
          ) : (
            <TraceabilityProducersTab traceability={trace.data} />
          )}
        </TabsContent>
        <TabsContent value="parcels" className="pt-4">
          {trace.isLoading || !trace.data ? (
            <TabSkeleton />
          ) : (
            <TraceabilityParcelsTab
              otId={otRow.id}
              traceability={trace.data}
              active={activeTab === "parcels"}
            />
          )}
        </TabsContent>
        <TabsContent value="deliveries" className="pt-4">
          {trace.isLoading || !trace.data ? (
            <TabSkeleton />
          ) : (
            <TraceabilityDeliveriesTab traceability={trace.data} />
          )}
        </TabsContent>
        <TabsContent value="transactions" className="pt-4">
          {trace.isLoading || !trace.data ? (
            <TabSkeleton />
          ) : (
            <TraceabilityTransactionsTab traceability={trace.data} />
          )}
        </TabsContent>
      </Tabs>

    </div>
  )
}

function PageSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-6">
      <Skeleton className="h-4 w-72" />
      <Skeleton className="h-8 w-96" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    </div>
  )
}

function TabSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
      <Skeleton className="h-24" />
    </div>
  )
}
